import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Search, Filter, RefreshCw, Trash2, EyeOff, Upload, Users, BookOpen, Settings, AlertCircle, ChevronLeft, ChevronRight, Calendar, Clock, User, Activity } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';
import { supabase } from '../../lib/supabase';

const ACTION_TYPES = {
  delete: { label: 'Delete', color: 'bg-destructive/15 text-destructive', icon: Trash2 },
  toggle: { label: 'Status Toggle', color: 'bg-warning/15 text-warning', icon: EyeOff },
  import: { label: 'Import', color: 'bg-success/15 text-success', icon: Upload },
  user_change: { label: 'User Change', color: 'bg-primary/15 text-primary', icon: Users },
  create: { label: 'Create', color: 'bg-accent/15 text-accent', icon: BookOpen },
  update: { label: 'Update', color: 'bg-secondary text-secondary-foreground', icon: Settings },
};

const RESOURCE_TYPES = ['bible_version', 'user', 'reading_plan', 'verse_list', 'import', 'award'];

const AdminAuditLog = () => {
  const navigate = useNavigate();
  const { userProfile, isDemoAdmin } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState('');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  useEffect(() => {
    checkAdminAccess();
  }, [userProfile, isDemoAdmin]);

  const checkAdminAccess = async () => {
    try {
      const contextIsAdmin = userProfile?.role === 'admin';
      const contextIsDemoAdmin = isDemoAdmin === true;
      if (contextIsAdmin || contextIsDemoAdmin) {
        setIsAdmin(true);
        return;
      }
      const adminStatus = await adminService?.isAdmin();
      if (adminStatus) {
        setIsAdmin(true);
        return;
      }
      setError('Access denied. Administrator privileges required.');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      setError('Access verification failed.');
      setTimeout(() => navigate('/dashboard'), 2000);
    }
  };

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      let query = supabase?.from('admin_audit_logs')?.select(`
          id,
          action_type,
          resource_type,
          resource_id,
          resource_name,
          details,
          created_at,
          admin_user_id,
          user_profiles!admin_audit_logs_admin_user_id_fkey(full_name, email)
        `, { count: 'exact' })?.order('created_at', { ascending: false })?.range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

      if (actionFilter) query = query?.eq('action_type', actionFilter);
      if (resourceFilter) query = query?.eq('resource_type', resourceFilter);
      if (dateFrom) query = query?.gte('created_at', dateFrom);
      if (dateTo) query = query?.lte('created_at', dateTo + 'T23:59:59');
      if (searchTerm) {
        query = query?.or(`resource_name.ilike.%${searchTerm}%,details.ilike.%${searchTerm}%`);
      }

      const { data, error: queryError, count } = await query;

      if (queryError) {
        // Table may not exist yet — show empty state gracefully
        if (queryError?.code === '42P01') {
          setLogs([]);
          setTotalCount(0);
          setError('Audit log table not yet created. Admin actions will be logged once the migration is applied.');
          return;
        }
        throw queryError;
      }

      setLogs(data || []);
      setTotalCount(count || 0);
    } catch (err) {
      console.error('Error loading audit logs:', err);
      setError('Failed to load audit logs: ' + err?.message);
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, resourceFilter, dateFrom, dateTo, searchTerm]);

  useEffect(() => {
    if (isAdmin) loadLogs();
  }, [isAdmin, loadLogs]);

  const handleSearch = (e) => {
    e?.preventDefault();
    setPage(1);
    loadLogs();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString)?.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getActionConfig = (actionType) => {
    return ACTION_TYPES?.[actionType] || { label: actionType, color: 'bg-muted text-muted-foreground', icon: Activity };
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  if (!isAdmin && !loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">{error || 'Verifying access...'}</h2>
          <p className="text-muted-foreground mb-4">You will be redirected shortly.</p>
          <button onClick={() => navigate('/dashboard')} className="text-sm text-primary hover:text-primary/80 underline">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card shadow-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-5">
            <div className="flex items-center gap-4">
              <Link
                to="/admin-dashboard"
                className="flex items-center text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Admin Dashboard
              </Link>
              <div className="h-6 border-l border-border" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
                  <Activity className="w-6 h-6 text-primary" />
                  Admin Audit Log
                </h1>
                <p className="text-sm text-muted-foreground">Track all administrative actions</p>
              </div>
            </div>
            <button
              onClick={() => { setPage(1); loadLogs(); }}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-lg hover:bg-muted/50"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters */}
        <div className="bg-card rounded-xl border border-border p-4 sm:p-5 mb-6 shadow-sm">
          <form onSubmit={handleSearch} className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by resource name or details..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="pl-9 pr-4 py-2 w-full border border-border rounded-lg bg-input text-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent"
                />
              </div>
              {/* Action Type */}
              <div className="relative">
                <Filter className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <select
                  value={actionFilter}
                  onChange={(e) => { setActionFilter(e?.target?.value); setPage(1); }}
                  className="pl-9 pr-8 py-2 border border-border rounded-lg bg-input text-foreground text-sm focus:ring-2 focus:ring-ring appearance-none"
                >
                  <option value="">All Actions</option>
                  {Object.entries(ACTION_TYPES)?.map(([key, val]) => (
                    <option key={key} value={key}>{val?.label}</option>
                  ))}
                </select>
              </div>
              {/* Resource Type */}
              <div className="relative">
                <BookOpen className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <select
                  value={resourceFilter}
                  onChange={(e) => { setResourceFilter(e?.target?.value); setPage(1); }}
                  className="pl-9 pr-8 py-2 border border-border rounded-lg bg-input text-foreground text-sm focus:ring-2 focus:ring-ring appearance-none"
                >
                  <option value="">All Resources</option>
                  {RESOURCE_TYPES?.map(r => (
                    <option key={r} value={r}>{r?.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
            </div>
            {/* Date range */}
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e?.target?.value); setPage(1); }}
                  className="px-3 py-2 border border-border rounded-lg bg-input text-foreground text-sm focus:ring-2 focus:ring-ring"
                />
                <span className="text-muted-foreground text-sm">to</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e?.target?.value); setPage(1); }}
                  className="px-3 py-2 border border-border rounded-lg bg-input text-foreground text-sm focus:ring-2 focus:ring-ring"
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Apply Filters
              </button>
              {(searchTerm || actionFilter || resourceFilter || dateFrom || dateTo) && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm(''); setActionFilter(''); setResourceFilter('');
                    setDateFrom(''); setDateTo(''); setPage(1);
                  }}
                  className="px-4 py-2 border border-border rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-6 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <span className="text-destructive text-sm">{error}</span>
          </div>
        )}

        {/* Summary bar */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-muted-foreground">
            {loading ? 'Loading...' : `${totalCount?.toLocaleString()} total log entries`}
          </p>
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages || 1}
          </p>
        </div>

        {/* Table */}
        <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-16 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-muted-foreground text-sm">Loading audit logs...</p>
            </div>
          ) : logs?.length === 0 ? (
            <div className="py-16 text-center">
              <Activity className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-muted-foreground font-medium">No audit log entries found</p>
              <p className="text-sm text-muted-foreground/60 mt-1">
                {searchTerm || actionFilter || resourceFilter ? 'Try adjusting your filters' : 'Admin actions will appear here once recorded'}
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Timestamp</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Admin</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Action</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Resource</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Details</th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {logs?.map((log) => {
                      const actionConfig = getActionConfig(log?.action_type);
                      const ActionIcon = actionConfig?.icon;
                      return (
                        <tr key={log?.id} className="hover:bg-muted/20 transition-colors">
                          <td className="px-5 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                              <Clock className="w-3.5 h-3.5 shrink-0" />
                              {formatDate(log?.created_at)}
                            </div>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                                <User className="w-3.5 h-3.5 text-primary" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-foreground">
                                  {log?.user_profiles?.full_name || 'Admin'}
                                </p>
                                <p className="text-xs text-muted-foreground">{log?.user_profiles?.email || '—'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${actionConfig?.color}`}>
                              <ActionIcon className="w-3 h-3" />
                              {actionConfig?.label}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <div>
                              <p className="text-sm font-medium text-foreground">{log?.resource_name || log?.resource_id || '—'}</p>
                              <p className="text-xs text-muted-foreground capitalize">{log?.resource_type?.replace(/_/g, ' ') || '—'}</p>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <p className="text-sm text-muted-foreground max-w-xs truncate" title={log?.details}>
                              {log?.details || '—'}
                            </p>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y divide-border">
                {logs?.map((log) => {
                  const actionConfig = getActionConfig(log?.action_type);
                  const ActionIcon = actionConfig?.icon;
                  return (
                    <div key={log?.id} className="p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${actionConfig?.color}`}>
                          <ActionIcon className="w-3 h-3" />
                          {actionConfig?.label}
                        </span>
                        <span className="text-xs text-muted-foreground">{formatDate(log?.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <span className="text-sm font-medium text-foreground">
                          {log?.user_profiles?.full_name || log?.user_profiles?.email || 'Admin'}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-foreground font-medium">{log?.resource_name || log?.resource_id || '—'}</p>
                        <p className="text-xs text-muted-foreground capitalize">{log?.resource_type?.replace(/_/g, ' ') || '—'}</p>
                      </div>
                      {log?.details && (
                        <p className="text-xs text-muted-foreground">{log?.details}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-5">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1.5 px-4 py-2 border border-border rounded-lg text-sm text-foreground hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = page - 2 + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                      page === pageNum
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted/50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="flex items-center gap-1.5 px-4 py-2 border border-border rounded-lg text-sm text-foreground hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAuditLog;
